import { Component, EventEmitter, Input, Output, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

import { SupportApiService } from '../core/support-api.service';

/** Spec, §Escalation — a mandatory four-field capture (summary, steps tried,
 * current state, reason) so the next tier gets full context without having
 * to re-derive it from the thread. Reuses the existing postMessage route as
 * an internal note (Ruling — no dedicated escalate route exists upstream);
 * `currentState` pre-fills from the ticket's current status + last message
 * summary but stays editable, matching the spec's "not required to be
 * retyped from scratch".
 *
 * Fields are signal-backed (not plain properties) for the same reason as
 * ComposeComponent's `body`: a signal write notifies its template's
 * two-way `[(ngModel)]` binding synchronously even when set directly on the
 * component instance outside a zone-tracked event (as this spec's tests
 * do) — a plain field mutation between two `detectChanges()` calls does
 * not flag the reactive consumer dirty, which trips a false-positive
 * NG0100 (ExpressionChangedAfterItHasBeenChecked). `currentState`'s
 * pre-fill is likewise computed lazily in its getter (from `currentStatus`
 * + `lastMessageSummary`, both plain `@Input()`s) rather than via
 * `ngOnChanges`, because that lifecycle hook never fires for a direct
 * instance assignment either — only for a template-bound `@Input`. */
@Component({
  selector: 'app-escalate-modal',
  standalone: true,
  imports: [FormsModule, NgbTooltip],
  template: `
    <div class="escalate-modal" data-testid="escalate-modal">
      <label>Summary
        <textarea [(ngModel)]="summary" data-testid="escalate-summary"
                  ngbTooltip="A short summary of the issue for the next tier."></textarea>
      </label>
      <label>Steps already tried
        <span ngbTooltip="What have you (or the customer) already attempted? This saves the next tier from repeating it.">?</span>
        <textarea [(ngModel)]="stepsTried" data-testid="escalate-steps-tried"></textarea>
      </label>
      <label>Current state (pre-filled, edit as needed)
        <textarea [(ngModel)]="currentState" data-testid="escalate-current-state"
                  ngbTooltip="Pre-filled from the ticket status and last message — edit before submitting."></textarea>
      </label>
      <label>Reason for escalation
        <textarea [(ngModel)]="reason" data-testid="escalate-reason"
                  ngbTooltip="Why does this need the next tier?"></textarea>
      </label>
      <button type="button" data-testid="escalate-submit" [disabled]="!allFieldsFilled()" (click)="submit()"
              ngbTooltip="Escalate this ticket with the details above as a permanent internal record">
        Escalate
      </button>
    </div>
  `,
})
export class EscalateModalComponent {
  private api = inject(SupportApiService);

  @Input() ticketId = '';
  @Input() currentStatus = '';
  @Input() lastMessageSummary = '';
  @Output() escalated = new EventEmitter<void>();

  private summarySignal = signal('');
  get summary(): string {
    return this.summarySignal();
  }
  set summary(value: string) {
    this.summarySignal.set(value);
  }

  private stepsTriedSignal = signal('');
  get stepsTried(): string {
    return this.stepsTriedSignal();
  }
  set stepsTried(value: string) {
    this.stepsTriedSignal.set(value);
  }

  // null = not yet edited by the user -> the getter derives the pre-fill
  // from currentStatus/lastMessageSummary; once set, the edited value wins.
  private currentStateSignal = signal<string | null>(null);
  get currentState(): string {
    return this.currentStateSignal() ?? `Status: ${this.currentStatus}. Last message: ${this.lastMessageSummary}`;
  }
  set currentState(value: string) {
    this.currentStateSignal.set(value);
  }

  private reasonSignal = signal('');
  get reason(): string {
    return this.reasonSignal();
  }
  set reason(value: string) {
    this.reasonSignal.set(value);
  }

  allFieldsFilled(): boolean {
    return [this.summary, this.stepsTried, this.currentState, this.reason].every((f) => f.trim().length > 0);
  }

  submit(): void {
    if (!this.allFieldsFilled()) return;
    const body = `Escalation\nSummary: ${this.summary}\nSteps tried: ${this.stepsTried}\n`
      + `Current state: ${this.currentState}\nReason: ${this.reason}`;
    this.api.postMessage(this.ticketId, body, 'internal').subscribe(() => this.escalated.emit());
  }
}
